import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  ActionIcon,
  Button,
  Group,
  LoadingOverlay,
  ScrollArea,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue,useDisclosure, useMounted } from "@mantine/hooks";
import { IconPencil, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { StringParam, useQueryParam, withDefault } from "use-query-params";
import { useShallow } from "zustand/react/shallow";

import { StyledLaboratory } from "@pages/Patients/Laboratory/Laboratory.styled";

import { openDeleteModal } from "@components/DeleteModal";
import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
} from "@components/Table";

import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { QP } from "@constants/index";

import { useDeleteClinicCollectionMethod } from "@hooks/mutations/clinic/useDeleteClinicCollectionMethod";
import { useDeleteClinicLaboratoryService } from "@hooks/mutations/clinic/useDeleteClinicLaboratoryService";
import { useGetClinicCollectionMethods } from "@hooks/queries/clinic/useGetClinicCollectionMethods";
import { useGetClinicLaboratoryServicesDetailed } from "@hooks/queries/clinic/useGetClinicLaboratoryServicesDetailed";
import { useGetPartners } from "@hooks/queries/clinic/useGetPartners";
import { useAppStore } from "@hooks/store/useAppStore";
import { useSyncTableQueryParams } from "@hooks/useSyncTableQueryParams";
import { useTableColumnCount } from "@hooks/useTableColumnCount";

import { ClinicLaboratoryService } from "@customTypes/clinic";

import { StyledResourceManagement } from "../../ResourceManagementTabs.styled";

import { AddAnalysisServiceModal } from "./components/AddAnalysisServiceModal";
import { AddCollectionMethodModal } from "./components/AddCollectionMethodModal";
import { EditAnalysisServiceModal } from "./components/EditAnalysisServiceModal";
import { EditCollectionMethodModal } from "./components/EditCollectionMethodModal";


// Collection Method interface (placeholder - should match actual API structure)
interface CollectionMethod {
  id: string;
  method: string;
  price: number;
  createdAt: Date;
  updatedAt: Date;
}

export const AnalysisTab = () => {
  const { t, i18n } = useTranslation();

  // Table column count for skeleton loading states
  const { tableRef, columnCount } = useTableColumnCount();

  // Use useAppStore for sub-tab state
  const [storeTabs, setStoreTabs] = useAppStore(
    useShallow(state => [state.tabs, state.setTabs])
  );

  const activeSubTab = storeTabs.resourceManagementSubTab || "analysis";

  // Navigation helper
  const goToCollectionMethods = () => {
    setStoreTabs({ resourceManagementSubTab: 'collection-methods' });
  };

  const [searchFilter, setSearchFilter] = useQueryParam(
    QP.SEARCH,
    withDefault(StringParam, "")
  );
  const { debounced, tablePagination, setTablePagination } =
    useSyncTableQueryParams({ searchFilter });

  // Collection methods search and pagination - simple state
  const [collectionMethodsSearch, setCollectionMethodsSearch] = useState("");
  const [collectionMethodsDebounced] = useDebouncedValue(collectionMethodsSearch, 400);
  const [collectionMethodsPagination, setCollectionMethodsPagination] = useState({
    page: 1,
    pageSize: 25,
    pageCount: 1,
    total: 0,
  });

  const [tableData, setTableData] = useState<ClinicLaboratoryService[]>([]);
  const [isMounted, setIsMounted] = useState(true);
  const mounted = useMounted();
  const [currentLaboratoryService, setCurrentLaboratoryService] =
    useState<ClinicLaboratoryService | null>(null);
  const [currentCollectionMethod, setCurrentCollectionMethod] =
    useState<CollectionMethod | null>(null);

  // API calls
  const {
    data: collectionMethodsResponse,
    isLoading: isCollectionMethodsLoading,
    fetchStatus: collectionMethodsFetchStatus,
    isError: isCollectionMethodsError,
    isSuccess: isCollectionMethodsSuccess,
  } = useGetClinicCollectionMethods({
    pagination: collectionMethodsPagination,
    search: collectionMethodsDebounced,
    isEnabled: activeSubTab === 'collection-methods',
  });

  const collectionMethodsData = collectionMethodsResponse?.newData || [];

  const {
    mutate: deleteLaboratoryService,
    isLoading: isDeleteLaboratoryServiceLoading,
  } = useDeleteClinicLaboratoryService();
  const {
    mutate: deleteCollectionMethod,
    isLoading: isDeleteCollectionMethodLoading,
  } = useDeleteClinicCollectionMethod();

  const [
    addAnalysisServiceModalOpened,
    { open: openAddAnalysisServiceModal, close: closeAddAnalysisServiceModal },
  ] = useDisclosure(false);

  const [
    editAnalysisServiceModalOpened,
    { open: openEditAnalysisServiceModal, close: closeEditAnalysisServiceModal },
  ] = useDisclosure(false);

  const [
    addCollectionMethodModalOpened,
    { open: openAddCollectionMethodModal, close: closeAddCollectionMethodModal },
  ] = useDisclosure(false);

  const [
    editCollectionMethodModalOpened,
    { open: openEditCollectionMethodModal, close: closeEditCollectionMethodModal },
  ] = useDisclosure(false);

  const { data, isLoading, isRefetching, fetchStatus, isError, isSuccess } = useGetClinicLaboratoryServicesDetailed({
    pagination: {
      page: tablePagination.page,
      pageSize: tablePagination.pageSize,
    },
    search: debounced.toLowerCase(),
  });

  const { data: partnersData } = useGetPartners();

  const partnersMap = useMemo(() => {
    const map: Record<string, string> = {};
    (partnersData || []).forEach(p => {
      map[p._id] = p.name;
    });
    return map;
  }, [partnersData]);


  // Collection Methods table rows (AdminDashboard pattern)
  const collectionMethodRows = useMemo(() => {
    return collectionMethodsData?.map(collectionMethod => (
      <Table.Tr key={collectionMethod.id}>
        <Table.Td>
          {(() => {
            const value = collectionMethod.method;
            const isLong = value && value.length > 30;
            return (
              <Tooltip label={isLong ? value : null} withArrow disabled={!isLong}>
                <Text
                  fz={14}
                  fw={500}
                  lh={1.5}
                  style={{
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                  {value}
                </Text>
              </Tooltip>
            );
          })()}
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5}>
            {collectionMethod.price || 0}
          </Text>
        </Table.Td>
        <Table.Td>
          <Group gap={8} justify="start">
            <Tooltip label={t("edit")} withArrow>
              <PermissionGate permission="resources.edit"><ActionIcon
                variant="outline"
                size="sm"
                onClick={() => {
                  setCurrentCollectionMethod(collectionMethod);
                  openEditCollectionMethodModal();
                }}
                disabled={false}>
                <IconPencil size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
            <Tooltip label={t("delete")} withArrow>
              <PermissionGate permission="resources.delete"><ActionIcon
                variant="outline"
                color="gray"
                size="sm"
                disabled={true}
                style={{ opacity: 0.5, cursor: 'not-allowed' }}
                onClick={() => {
                  openDeleteModal({
                    title: t("delete-collection-method"),
                    textNode: (
                      <Text size="sm" lh={1.5}>
                        <Trans
                          i18nKey="delete-collection-method-confirmation"
                          values={{ methodName: collectionMethod.method }}
                          components={{ bold: <Text span fw={700} /> }}
                        />
                      </Text>
                    ),
                    confirmText: t("delete-collection-method"),
                    onConfirm: () => {
                      deleteCollectionMethod(collectionMethod.id);
                    },
                  });
                }}>
                <IconTrash size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [collectionMethodsData, i18n.language]);

  // Laboratory Services table rows (AdminDashboard pattern)
  const laboratoryServiceRows = useMemo(() => {
    return tableData?.map(laboratoryService => (
      <Table.Tr key={laboratoryService.id}>
        <Table.Td>
          {(() => {
            const value = laboratoryService.type;
            const isLong = value && value.length > 30;
            return (
              <Tooltip label={isLong ? value : null} withArrow disabled={!isLong}>
                <Text
                  fz={14}
                  fw={500}
                  lh={1.5}
                  style={{
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                  {value}
                </Text>
              </Tooltip>
            );
          })()}
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5}>
            {laboratoryService.partner_id ? partnersMap[laboratoryService.partner_id] || "-" : "-"}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5}>
            {laboratoryService.price || 0}
          </Text>
        </Table.Td>
        <Table.Td>
          {(() => {
            const value = laboratoryService.collectionMethod || "-";
            const isLong = value && value.length > 20;
            return (
              <Tooltip label={isLong ? value : null} withArrow disabled={!isLong}>
                <Text
                  fz={14}
                  fw={400}
                  lh={1.5}
                  c="dimmed"
                  style={{
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                  {value}
                </Text>
              </Tooltip>
            );
          })()}
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5}>
            {laboratoryService.collectionPrice || 0}
          </Text>
        </Table.Td>
        <Table.Td>
          {(() => {
            const value = laboratoryService.category || "-";
            const isLong = value && value.length > 15;
            return (
              <Tooltip label={isLong ? value : null} withArrow disabled={!isLong}>
                <Text
                  fz={14}
                  fw={400}
                  lh={1.5}
                  c="dimmed"
                  style={{
                    maxWidth: '120px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                  {value}
                </Text>
              </Tooltip>
            );
          })()}
        </Table.Td>
        <Table.Td>
          <Group gap={8} justify="start">
            <Tooltip label={t("edit")} withArrow>
              <PermissionGate permission="resources.edit"><ActionIcon
                variant="outline"
                size="sm"
                onClick={() => {
                  setCurrentLaboratoryService(laboratoryService);
                  openEditAnalysisServiceModal();
                }}
              >
                <IconPencil size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
            <Tooltip label={t("delete")} withArrow>
              <PermissionGate permission="resources.delete"><ActionIcon
                variant="outline"
                color="gray"
                size="sm"
                disabled={true}
                style={{ opacity: 0.5, cursor: 'not-allowed' }}
                onClick={() => {
                  openDeleteModal({
                    title: t("delete-laboratory-service"),
                    textNode: (
                      <Text size="sm" lh={1.5}>
                        <Trans
                          i18nKey="delete-laboratory-service-confirmation"
                          values={{ serviceName: laboratoryService.type }}
                          components={{ bold: <Text span fw={700} /> }}
                        />
                      </Text>
                    ),
                    confirmText: t("delete-service"),
                    onConfirm: () => {
                      deleteLaboratoryService(laboratoryService.id);
                    },
                  });
                }}>
                <IconTrash size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [tableData, i18n.language]);



  useEffect(() => {
    if (data) {
      setTableData(data.newData);
      setTablePagination(data.pagination);
    } else {
      setTableData([]);
    }
  }, [data, setTablePagination]);

  useEffect(() => {
    if (collectionMethodsResponse) {
      setCollectionMethodsPagination(collectionMethodsResponse.pagination);
    }
  }, [collectionMethodsResponse]);

  // Reset collection methods search when switching away from collection methods tab
  useEffect(() => {
    if (activeSubTab !== 'collection-methods') {
      setCollectionMethodsSearch("");
    }
  }, [activeSubTab]);

  useEffect(() => {
    if (mounted && isRefetching) return;
    if (mounted && !isRefetching) {
      setIsMounted(false);
    }
  }, [isRefetching, mounted]);

  return (
    <StyledLaboratory>
      <StyledResourceManagement>
        <LoadingOverlay
          visible={isRefetching && !!data?.newData?.length && !isMounted}
          zIndex={1000}
          overlayProps={{ blur: 0.1, opacity: 0.6 }}
          loaderProps={{ display: "none" }}
        />

        <div className="header">
          <Group w={"100%"} justify="space-between" align="center">
            <h1>{activeSubTab === "analysis" ? t("laboratory") : t("collection-methods")}</h1>
            <Group gap={16} align="center">
              {activeSubTab === 'analysis' && (
                <>
                  <PermissionGate permission="resources.create"><Button
                    leftSection={<IconPlus size={16} />}
                    onClick={openAddAnalysisServiceModal}>
                    {t("add-lab")}
                  </Button></PermissionGate>
                  <Text
                    fz={14}
                    c="primary.6"
                    style={{ cursor: 'pointer' }}
                    onClick={goToCollectionMethods}>
                    {t("collection-methods")} {'>>'}
                  </Text>
                </>
              )}
              {activeSubTab === 'collection-methods' && (
                <PermissionGate permission="resources.create"><Button
                  leftSection={<IconPlus size={16} />}
                  onClick={openAddCollectionMethodModal}>
                  {t("add-collection-method")}
                </Button></PermissionGate>
              )}
            </Group>
          </Group>
        </div>

        <TextInput
          mt={"md"}
          placeholder={activeSubTab === 'analysis'
            ? t("search-for-a-laboratory-service")
            : t("search-for-a-collection-method")}
          leftSection={<IconSearch size={16} />}
          value={activeSubTab === 'analysis' ? (searchFilter || "") : collectionMethodsSearch}
          onChange={event => {
            if (activeSubTab === 'analysis') {
              setSearchFilter(event.currentTarget.value || null);
            } else {
              setCollectionMethodsSearch(event.currentTarget.value);
            }
          }}
          style={{ flex: 1, maxWidth: 400 }}
        />

        <div style={{paddingBottom: "200px"}}>
          <ScrollArea maw={{ base: 700, md: 1024, lg1: "100%" }}>
            {activeSubTab === 'analysis' ? (
              <>
                <Table
                  ref={tableRef}
                  withRowBorders={true}
                  mt={"1rem"}
                  miw={1280}
                  withTableBorder
                  highlightOnHover
                  highlightOnHoverColor="var(--clr-gray-100)">
                  <Table.Thead>
                    <Table.Tr bg={"var(--clr-gray-100)"}>
                      <Table.Th>{t("type")}</Table.Th>
                      <Table.Th>{t("partner")}</Table.Th>
                      <Table.Th>{t("price")}</Table.Th>
                      <Table.Th>{t("collection-method")}</Table.Th>
                      <Table.Th>{t("collection-price")}</Table.Th>
                      <Table.Th>{t("category")}</Table.Th>
                      <Table.Th>{t("action")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {renderLoadingErrorData({
                      isLoading: isLoading && fetchStatus === "fetching",
                      isError,
                      isSuccess,
                      dataLength: tableData.length,
                      errorComponent: (
                        <TableErrorRow
                          colSpan={7}
                          message={t("encountered-error")}
                        />
                      ),
                      loadingComponent: (
                        <TableSkeletonRows
                          columns={7}
                          skeletonProps={{
                            widths: ["lg", "md", "md", "md", "md", "md", "sm"],
                          }}
                        />
                      ),
                      emptyDataComponent: (
                        <TableEmptyRow
                          colSpan={7}
                          message={
                            debounced
                              ? t("no-laboratory-services-found")
                              : t("no-laboratory-services-available")
                          }
                        />
                      ),
                      dataComponent: laboratoryServiceRows,
                    })}
                  </Table.Tbody>
                </Table>
              </>
            ) : (
              <>
                <Table
                  ref={tableRef}
                  withRowBorders={true}
                  mt={"1rem"}
                  miw={1280}
                  withTableBorder
                  highlightOnHover
                  highlightOnHoverColor="var(--clr-gray-100)">
                  <Table.Thead>
                    <Table.Tr bg={"var(--clr-gray-100)"}>
                      <Table.Th>{t("collection-method")}</Table.Th>
                      <Table.Th>{t("collection-price")}</Table.Th>
                      <Table.Th>{t("action")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {renderLoadingErrorData({
                      isLoading: isCollectionMethodsLoading && collectionMethodsFetchStatus === "fetching",
                      isError: isCollectionMethodsError,
                      isSuccess: isCollectionMethodsSuccess,
                      dataLength: collectionMethodsData.length,
                      errorComponent: (
                        <TableErrorRow
                          colSpan={3}
                          message={t("encountered-error")}
                        />
                      ),
                      loadingComponent: (
                        <TableSkeletonRows
                          columns={3}
                          skeletonProps={{
                            widths: ["lg", "md", "md"],
                          }}
                        />
                      ),
                      emptyDataComponent: (
                        <TableEmptyRow
                          colSpan={3}
                          message={
                            collectionMethodsDebounced
                              ? t("no-collection-methods-found")
                              : t("no-collection-methods-available")
                          }
                        />
                      ),
                      dataComponent: collectionMethodRows,
                    })}
                  </Table.Tbody>
                </Table>
              </>
            )}
          </ScrollArea>

          {/* Pagination outside ScrollArea */}
          {activeSubTab === 'analysis' && data && !(isLoading && fetchStatus === "fetching") && (
            <TablePagination
              paginationData={data?.pagination || {}}
              paginatedRows={data?.newData?.length || 0}
              onPageChange={(page) => setTablePagination(prev => ({ ...prev, page }))}
              isLoading={isLoading}
            />
          )}

          {activeSubTab === 'collection-methods' && collectionMethodsResponse && !isCollectionMethodsLoading && (
            <TablePagination
              paginationData={collectionMethodsResponse?.pagination || {}}
              paginatedRows={collectionMethodsResponse?.newData?.length || 0}
              onPageChange={(page) => setCollectionMethodsPagination(prev => ({ ...prev, page }))}
              isLoading={isCollectionMethodsLoading}
            />
          )}
        </div>

        <AddAnalysisServiceModal
          opened={addAnalysisServiceModalOpened}
          close={closeAddAnalysisServiceModal}
        />

        <EditAnalysisServiceModal
          opened={editAnalysisServiceModalOpened}
          close={closeEditAnalysisServiceModal}
          laboratoryService={currentLaboratoryService}
        />

        <AddCollectionMethodModal
          opened={addCollectionMethodModalOpened}
          close={closeAddCollectionMethodModal}
        />

        <EditCollectionMethodModal
          opened={editCollectionMethodModalOpened}
          close={closeEditCollectionMethodModal}
          collectionMethod={currentCollectionMethod}
        />
      </StyledResourceManagement>
    </StyledLaboratory>
  );
};
